import { defer, LoaderFunctionArgs } from 'react-router-dom';
import { QueryClient, QueryFunction } from '@tanstack/react-query';

import { useBreadcrumbsStore } from 'stores/breadcrumbs/breadcrumbs';
import { BreadcrumbConfig, BreadcrumbHandle } from 'support/types/breadcrumbs';

import { useGenerateBreadcrumbs, useProcessedMatches, useSavePreviousMatches } from './utils';

type LoaderFunction = (queryClient: QueryClient) => (args: LoaderFunctionArgs) => Promise<ReturnType<typeof defer>>;

export function createLoader<TResult>(
  getQueryKey: (args: LoaderFunctionArgs) => ReadonlyArray<unknown>,
  queryFn: QueryFunction<TResult, any>,
): LoaderFunction {
  return (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
    const queryKey = getQueryKey(args);
    const queryData = queryClient.getQueryData<TResult>(queryKey);
    return defer({
      data: queryData !== undefined ? Promise.resolve(queryData) : queryClient.fetchQuery(queryKey, queryFn),
    });
  };
}

export const createHandle = <TData, TParams = unknown>(
  callback: BreadcrumbHandle<TData, TParams>['getBreadcrumbConfig'],
): BreadcrumbHandle<TData, TParams> => {
  return {
    getBreadcrumbConfig: callback,
  };
};

export const useBreadcrumbs = () => {
  const breadcrumbs = useBreadcrumbsStore((state) => state.breadcrumbs);
  const matches = useProcessedMatches();

  useGenerateBreadcrumbs(matches);
  useSavePreviousMatches(matches);

  const validBreadcrumbs = breadcrumbs.filter((breadcrumb): breadcrumb is BreadcrumbConfig => breadcrumb !== undefined);

  return validBreadcrumbs;
};
